import { Component, computed, effect, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';

import { finalize, Subject, takeUntil } from 'rxjs';
import { Account, UpdateAccountDto } from '@olv/ui-shared/models/account.interface';
import { AccountService } from '@olv/ui-shared/services/account.service';
import { UserProfile, AuthService } from '@olv/ui-shared/services/auth.service';
import { LoadingSpinnerComponent } from '../loading-spinner/loading-spinner.component';
import { ButtonComponent } from '../button/button.component';
import { AccountPhotoComponent } from '@olv/ui-shared/features/user-management';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { AppContextService } from '../../services/app-context.service';
import { ClientDetailsResponse, MerchantService } from '../../services/merchant.service';
import { OtpVerificationDialogComponent } from '../otp-verification-dialog/otp-verification-dialog.component';
import { Resources, Scopes } from '../../models/settlement-permissions.model';
import { TextInputComponent } from '../text-input/text-input.component';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { ToastService } from '@olv/ui-shared/ui-shared/toast/toast.service';
import { FormsModule } from '@angular/forms';

@Component({
  selector: 'app-user-info',
  templateUrl: './user-info.component.html',
  styleUrls: ['./user-info.component.scss'],
  standalone: true,
  imports: [
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatMenuTrigger,
    MatMenu,
    LoadingSpinnerComponent,
    TextInputComponent,
    FormsModule,
    ButtonComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class UserInfoComponent implements OnInit {
  protected readonly Resources = Resources;
  protected readonly Scopes = Scopes;

  user: UserProfile | null = null;
  account: Account | null = null;
  avatarSrc: string | null = null;
  groupNames = '';
  photoPreviewUrl: string | null = null;
  photoFile: File | null = null;
  loading = false;
  canChangingPhoto = false;

  clientId = signal<string | null>(null);
  secretCode = signal<string | null>(null);
  showClientDetails = signal(false);
  hideSecret = signal(true);
  isSettlementApp = computed(() => this.appCtx.getAppName() === 'settlement');

  private destroy$ = new Subject<void>();
  private accountService = inject(AccountService);
  private authService = inject(AuthService);
  private dialog = inject(MatDialog);
  private appCtx = inject(AppContextService);
  private toastrService = inject(ToastService);
  private merchantService = inject(MerchantService);

  canViewClient = computed(() => this.authService.hasPermission(Resources.Merchants, Scopes.ReadClientCredentials));
  canResetClientSecret = computed(() => this.authService.hasPermission(Resources.Merchants, Scopes.ResetClientCecret));

  constructor() {
    effect(() => {
      const updated = this.accountService.profileUpdated();
      if (updated) {
        this.loadAccountDetails();
      }
    });
  }

  ngOnInit(): void {
    this.loading = true;
    this.authService.currentUser$.subscribe((user) => {
      if (user) {
        this.loadAccountDetails();
      }
    });

    this.canChangingPhoto = this.authService.hasPermission(Resources.Accounts, Scopes.Update);
  }

  loadClientDetails(): void {
    this.merchantService.getClientDetails().subscribe({
      next: (response: ClientDetailsResponse) => {
        this.clientId.set(response.clientId);
        this.secretCode.set(response.clientSecret);

        this.showClientDetails.set(true);
      },
      error: () => {
        this.showClientDetails.set(false);
        this.toastrService.error('Failed to load client details');
      },
    });
  }

  loadAccountDetails(): void {
    this.accountService
      .getAccountProfile()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe({
        next: (account) => {
          this.account = account;
          this.groupNames = account.groups?.map((group) => group.name).join('/') || '';
          this.avatarSrc = account.avatarUrl || null;
        },
      });
  }
  getUserInitial(): string {
    const displayName = this.user?.name || this.user?.username || 'U';
    return displayName.charAt(0).toUpperCase();
  }

  toggleSecret(): void {
    this.hideSecret.set(!this.hideSecret());
  }

  onShowClientDetails(): void {
    if (this.showClientDetails()) {
      this.showClientDetails.set(false);
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: false,
      maxWidth: '95vw',
      panelClass: 'custom-dialog-container',
      data: {
        title: 'Show API Credentials',
        message: 'The API Credentials details will be visible.',
      },
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;

      this.loadClientDetails();
    });
  }

  resetSecret(): void {
    if (!this.canResetClientSecret()) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: false,
      maxWidth: '95vw',
      panelClass: 'custom-dialog-container',
      data: {
        title: 'Reset Secret Code',
        message: 'The current secret code will be invalidated.',
      },
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (!confirmed) return;

      this.openOtp();
    });
  }

  openOtp(): void {
    const dialogRef = this.dialog.open(OtpVerificationDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      maxHeight: '95vh',
      disableClose: false,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
      data: {
        title: 'Verify Reset Secret Code',
      },
    });

    const sub = dialogRef.componentInstance.submitOtp.subscribe((otp: string) => {
      this.onOtpSubmit(otp, dialogRef);
    });

    dialogRef.afterClosed().subscribe(() => {
      sub.unsubscribe();
    });
  }

  private onOtpSubmit(otp: string, dialogRef: MatDialogRef<OtpVerificationDialogComponent>): void {
    const dialog = dialogRef.componentInstance;
    dialog.setLoadingState(true);
    dialogRef.disableClose = true;

    this.merchantService
      .resetClientSecret(otp)
      .pipe(
        finalize(() => {
          dialog.setLoadingState(false);
          dialogRef.disableClose = false;
        }),
      )
      .subscribe({
        next: () => {
          dialogRef.close();
          this.hideSecret.set(true);
          this.loadClientDetails();
          this.toastrService.success('Secret code reset successfully');
        },
        error: (error) => {
          if (error.isOtpError) {
            dialog.setErrorState(error.error?.message);
            dialog.otpForm.reset();
            dialog.focusInput(0);
            return;
          }

          dialogRef.close();
          this.toastrService.error('Failed to reset secret code');
        },
      });
  }

  openPhotoEditor(): void {
    const dialogRef = this.dialog.open(AccountPhotoComponent, {
      width: '600px',
      maxHeight: '95vh',
      maxWidth: '95vw',
      disableClose: false,
      autoFocus: false,
      panelClass: 'custom-dialog-container',
    });

    dialogRef.afterClosed().subscribe((file: File | undefined) => {
      if (!file) return;
      this.onPhotoReady(file);
    });
  }

  onPhotoReady(file: File): void {
    this.loading = true;

    if (this.photoPreviewUrl) {
      URL.revokeObjectURL(this.photoPreviewUrl);
    }
    this.photoFile = file;
    this.photoPreviewUrl = URL.createObjectURL(file);
    this.onSavePhoto();
  }
  onSavePhoto(): void {
    // If no new photo has been selected, nothing to do
    if (!this.photoFile || !this.account) {
      return;
    }

    this.loading = true;
    const updateData: UpdateAccountDto = { profileFile: this.photoFile };

    this.accountService
      .updateAccount(this.account.id!, updateData)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: () => {
          // Clear out photo inputs/preview
          this.photoFile = null;
          this.photoPreviewUrl = null;
        },
      });
  }

  onMenuClosed(): void {
    this.showClientDetails.set(false);
    this.hideSecret.set(true);
  }

  logout(): void {
    this.authService.logout();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
